// SurvillEmployeeLogin.jsx - WITH FORGOT PASSWORD MODAL
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { loginThunk } from "../features/auth/authThunks";
import { useNavigate } from "react-router-dom";
import deviceService from "../services/deviceService";
import ForgotPasswordModal from "../Component/ForgotPasswordModal";
import "../CSS/SurvillEmployeeLogin.css";

function SurvillEmployeeLogin() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { token, loading, error } = useSelector((state) => state.auth);
  
  const [credentials, setCredentials] = useState({
    username: "",
    password: "",
  });

  const [deviceStatus, setDeviceStatus] = useState({
    checking: true,
    agentRunning: false,
    hasDeviceKey: false,
    error: null,
  });

  // Modal state
  const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false);

  // Check device agent status on component mount
  useEffect(() => {
    checkDeviceStatus();
  }, []);

  // If user is already logged in, redirect them away from the login page
  useEffect(() => {
    if (token) {
      navigate("/dashboard");
    }
  }, [token, navigate]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isForgotPasswordOpen) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    return () => document.body.classList.remove('modal-open');
  }, [isForgotPasswordOpen]);

  const checkDeviceStatus = async () => {
    setDeviceStatus({ checking: true, agentRunning: false, hasDeviceKey: false, error: null });
    
    try {
      // Check if agent is running
      const agentRunning = await deviceService.isAgentRunning();
      
      if (!agentRunning) {
        setDeviceStatus({
          checking: false,
          agentRunning: false,
          hasDeviceKey: false,
          error: "Device agent not running. Admins can still login.",
        });
        return;
      }

      // Check if device key exists
      const hasKey = await deviceService.verifyKeyExists();
      
      setDeviceStatus({
        checking: false,
        agentRunning: true,
        hasDeviceKey: hasKey,
        error: hasKey ? null : "Device key not found. Contact your administrator.",
      });
    } catch (error) {
      console.error("Device status check error:", error);
      setDeviceStatus({
        checking: false,
        agentRunning: false,
        hasDeviceKey: false,
        error: "Could not verify device status. Admins can still login.",
      });
    }
  };

  const handleChange = (e) => {
    setCredentials({
      ...credentials,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Allow login attempt even if device agent is not running
    // Backend will determine if user is allowed (admins can login from anywhere)
    dispatch(loginThunk(credentials));
  };

  const openForgotPasswordModal = (e) => {
    e.preventDefault();
    setIsForgotPasswordOpen(true);
  };

  const closeForgotPasswordModal = () => {
    setIsForgotPasswordOpen(false);
  };

  return (
    <div className="survill-login">
      {/* Background/Illustration Section */}
      <div className="survill-background">
        <div className="survill-logo">
          {/* Company logo or illustration can be added here */}
        </div>
      </div>

      {/* Login Box */}
      <div className="login-box">
        <h2>Sign In</h2>
        
        {/* Device Status Indicator */}
        {deviceStatus.checking ? (
          <div className="">
            <div className="status-icon"></div>
            <div className="status-text"></div>
          </div>
        ) : deviceStatus.agentRunning && deviceStatus.hasDeviceKey ? (
          <div className="device-status authorized">
            <div className="status-icon"></div>
            <div className="status-text"></div>
          </div>
        ) : (
          <div className="device-status warning">
            <div className="status-icon">⚠</div>
            <div className="status-text">{deviceStatus.error}</div>
            {!deviceStatus.agentRunning && (
              <div className="status-help">
                <button 
                  type="button" 
                  onClick={checkDeviceStatus}
                  className="retry-button"
                >
                  Retry
                </button>
                <small className="admin-notice">
                  Administrators can login from any device
                </small>
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="input-container">
            <input
              id="survill-username"
              type="text"
              name="username"
              placeholder="Username"
              value={credentials.username}
              onChange={handleChange}
              required
            />
          </div>
          <div className="input-container">
            <input
              id="survill-password"
              type="password"
              name="password"
              placeholder="Password"
              value={credentials.password}
              onChange={handleChange}
              required
            />
          </div>
          <button 
            type="submit" 
            className="login-btn" 
            disabled={loading}
          >
            {loading ? "Logging in..." : "Login"}
          </button>
        </form>
        
        {error && (
          <div className="error-message">
            {error}
            {error.toLowerCase().includes('device') && !error.toLowerCase().includes('admin') && (
              <div className="error-help">
                <small>
                  Employees must use authorized devices.
                </small>
              </div>
            )}
          </div>
        )}
        
        {/* Forgot Password Link - Opens Modal */}
        <div className="login-links">
          <a 
            href="#" 
            className="forgot-password-link"
            onClick={openForgotPasswordModal}
          >
            Forgot Password?
          </a>
        </div>
        
        <div className="device-info">
          <small>
            Employees must use authorized devices. 
          </small>
        </div>
      </div>

      {/* Forgot Password Modal */}
      <ForgotPasswordModal 
        isOpen={isForgotPasswordOpen}
        onClose={closeForgotPasswordModal}
      />
    </div>
  );
}

export default SurvillEmployeeLogin;